import { useState, useCallback } from 'react';
import { ChevronRight, ChevronDown, Building2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

/**
 * Flattens a tree into a list of { node, level } objects for non-recursive rendering.
 */
function flattenTree(nodes, expandedIds, level = 0) {
  const result = [];
  for (const node of nodes) {
    result.push({ node, level });
    const hasChildren = node.children && node.children.length > 0;
    if (hasChildren && expandedIds.has(node.id)) {
      const childItems = flattenTree(node.children, expandedIds, level + 1);
      result.push(...childItems);
    }
  }
  return result;
}

export function TreeView({ nodes, onSelect, selectedId }) {
  const [expandedIds, setExpandedIds] = useState(() => {
    // Auto-expand root level
    const ids = new Set();
    if (nodes) {
      for (const n of nodes) { ids.add(n.id); }
    }
    return ids;
  });

  const toggleExpand = useCallback((id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  }, []);

  if (!nodes || nodes.length === 0) {
    return <p className="text-center text-xs text-slate-400 py-4">Keine Institutionen</p>;
  }

  const flat = flattenTree(nodes, expandedIds);

  return (
    <div data-testid="institution-tree">
      {flat.map(({ node, level }) => {
        const hasChildren = node.children && node.children.length > 0;
        const isExpanded = expandedIds.has(node.id);
        const isSelected = node.id === selectedId;

        return (
          <div
            key={node.id}
            data-testid={`tree-node-${node.id}`}
            onClick={() => onSelect(node)}
            className={`flex items-center gap-1.5 py-1.5 px-2 rounded cursor-pointer text-sm transition-colors
              ${isSelected ? 'bg-blue-100 text-blue-800' : 'hover:bg-slate-100 text-slate-700'}`}
            style={{ paddingLeft: `${level * 20 + 8}px` }}
          >
            {hasChildren ? (
              <button onClick={(e) => { e.stopPropagation(); toggleExpand(node.id); }} className="p-0.5">
                {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              </button>
            ) : (
              <span className="w-4.5" />
            )}
            <Building2 className={`w-3.5 h-3.5 flex-shrink-0 ${node.abteilung ? 'text-blue-500' : 'text-slate-500'}`} />
            <span className="truncate font-medium">{node.name}</span>
            {node.abteilung && <Badge variant="outline" className="text-[10px] px-1 py-0 ml-1">{node.abteilung}</Badge>}
            {hasChildren && <Badge variant="secondary" className="text-[10px] px-1 py-0 ml-auto">{node.children.length}</Badge>}
          </div>
        );
      })}
    </div>
  );
}
